import type { MigrationReportResponse } from '@hesabat/contracts';
import { Icon } from './icons';
import { formatMoney } from './money';

/**
 * تقرير الترحيل كما ثُبّت لحظة الاعتماد: الأعداد والإجماليات والمطابقات والقيد. يُطبع من
 * المتصفح نفسه (العربية والاتجاه صحيحان)، ويُحفظ PDF ليرفق بملف الشركة.
 */
const STATUS = { draft: 'مسودة', committed: 'معتمدة', reversed: 'معكوسة' } as const;
const CHECK_LABEL = { ok: 'سليم', fail: 'فشل', warn: 'تنبيه', na: 'لا ينطبق' } as const;

export default function MigrationReport({
  report,
  companyName,
  onBack,
}: {
  report: MigrationReportResponse;
  companyName?: string;
  onBack: () => void;
}) {
  const { batch, counts, totals } = report;
  const rows: [string, { created: number; linked: number }][] = [
    ['حسابات', counts.accounts],
    ['عملاء', counts.customers],
    ['موردون', counts.suppliers],
    ['أصناف', counts.items],
    ['خزائن', counts.cashboxes],
  ];
  return (
    <div className="panel mig-report">
      <div className="panel-head no-print">
        <h2>تقرير دفعة الترحيل</h2>
        <div className="panel-actions">
          <button type="button" className="ghost" onClick={onBack}>
            كل الدفعات
          </button>
          <button type="button" className="primary" onClick={() => window.print()}>
            طباعة / PDF
          </button>
        </div>
      </div>

      <header className="mig-report-head">
        {companyName && <p className="page-eyebrow">{companyName}</p>}
        <h3>
          {batch.number} — {batch.title}
        </h3>
        <p className="sub">
          تاريخ البداية <span className="mig-ltr">{batch.cutoverDate}</span>
          {batch.sourceSystem ? ' — من ' + batch.sourceSystem : ''} — الحالة: {STATUS[batch.status]}
          {batch.journalNumber && (
            <>
              {' '}
              — القيد <span className="mig-ltr">{batch.journalNumber}</span>
            </>
          )}
          {batch.reversalJournalNumber && (
            <>
              {' '}
              — قيد العكس <span className="mig-ltr">{batch.reversalJournalNumber}</span>
            </>
          )}
        </p>
        <p className="sub">
          اعتُمدت {batch.committedAt ? new Date(batch.committedAt).toLocaleString('ar-EG') : '—'}
          {batch.operator === 'platform' ? ' بواسطة مشغّل المنصة كخدمة' : ''}
          {batch.reversedAt &&
            ' — عُكست ' +
              new Date(batch.reversedAt).toLocaleString('ar-EG') +
              ': ' +
              (batch.reversalReason ?? '')}
        </p>
      </header>

      <div className="cards">
        <div className="card">
          <div className="label">مدين الميزان</div>
          <div className="num">{formatMoney(totals.trialBalanceDebit)}</div>
        </div>
        <div className="card">
          <div className="label">دائن الميزان</div>
          <div className="num">{formatMoney(totals.trialBalanceCredit)}</div>
        </div>
        <div className="card">
          <div className="label">أرصدة العملاء</div>
          <div className="num">{formatMoney(totals.receivables)}</div>
        </div>
        <div className="card">
          <div className="label">أرصدة الموردين</div>
          <div className="num">{formatMoney(totals.payables)}</div>
        </div>
        <div className="card">
          <div className="label">قيمة المخزون ({report.stock.items} صنف)</div>
          <div className="num">{formatMoney(report.stock.value)}</div>
        </div>
      </div>

      <div className="mig-report-grid">
        <section>
          <h4>الأعداد</h4>
          <table>
            <thead>
              <tr>
                <th>البيان</th>
                <th>أُنشئ</th>
                <th>رُبط بقائم</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(([label, count]) => (
                <tr key={label}>
                  <td>{label}</td>
                  <td className="num-cell">{count.created}</td>
                  <td className="num-cell">{count.linked}</td>
                </tr>
              ))}
              <tr>
                <td>صفوف مدموجة / مستبعدة</td>
                <td className="num-cell" colSpan={2}>
                  {counts.mergedRows} / {counts.deletedRows}
                </td>
              </tr>
            </tbody>
          </table>
          {report.journal && (
            <p className="sub">
              القيد <span className="mig-ltr">{report.journal.number}</span>: {report.journal.lines}{' '}
              سطرًا، مدين {formatMoney(report.journal.totalDebit)} = دائن{' '}
              {formatMoney(report.journal.totalCredit)}.
            </p>
          )}
        </section>
        <section>
          <h4>المطابقات عند الاعتماد</h4>
          <ul className="mig-checks compact">
            {report.checks.map((check) => (
              <li key={check.code} className={'mig-check ' + check.status}>
                <Icon
                  name={
                    check.status === 'ok' ? 'check' : check.status === 'fail' ? 'alert' : 'info'
                  }
                  size={16}
                />
                <div className="grow">
                  <strong>{check.title}</strong> — {CHECK_LABEL[check.status]}
                  <p>{check.detail}</p>
                </div>
              </li>
            ))}
          </ul>
        </section>
      </div>

      {report.notes.length > 0 && (
        <section>
          <h4>ملاحظات</h4>
          <ul>
            {report.notes.map((note) => (
              <li key={note}>{note}</li>
            ))}
          </ul>
        </section>
      )}
      <p className="sub mig-report-foot">
        صدر التقرير {new Date(report.generatedAt).toLocaleString('ar-EG')} — حسابات نواة. الأرصدة
        الافتتاحية تُراجع مع المحاسب القانونى قبل الاعتماد النهائى للقوائم.
      </p>
    </div>
  );
}
